import { HomePageDocument, TimesheetReferenceContractsDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { nowBackendDateTime } from 'modules/core/lib/dates'

import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'
import Dashboard from 'modules/dashboard/components/Dashboard'

const HomePage = () => {
  const { company, employee } = useCurrentSession()

  const companyId = company.id

  const attendancePolicyId = employee?.attendanceEmployeesPolicyId

  const variables = useMemo(
    () => ({
      companyId,
      today: nowBackendDateTime(),
      employeeAttendancePolicyIds: attendancePolicyId ? [attendancePolicyId] : undefined,
    }),
    [companyId, attendancePolicyId]
  )

  const { data, loading } = useQuery(HomePageDocument, {
    variables,
  })

  const companyLinksVisible = !!data?.dashboard._policies.dashboardWidgetCompanyLinksVisible.can
  const timeTrackingVisible = !!data?.attendance._policies.dashboardWidgetTimeTrackingVisible.can
  const canSeeOwnedAttendance = !!data?.attendance._policies?.companySeeOwned?.can

  const communitiesEnabled = !!data?.companies?.featureAvailabilitiesConnection.nodes?.some(
    (feature) => feature.id === 'communities_core' && feature.available
  )

  const timeTrackingManagementEnabled =
    !!data?.companies.featureAvailabilitiesConnection.nodes?.some(
      (feature) => feature.id === 'time_tracking_management' && feature.available
    )
  const pulseQuestionFeatureAvailabilityEnabled =
    !!data?.companies.featureAvailabilitiesConnection.nodes?.some(
      (feature) => feature.id === 'engagement_employee_pulse_questions' && feature.available
    )

  const enabledFeatures = data?.features?.featuresConnection?.nodes ?? []

  const pulseQuestionFeatureEnabled =
    !!enabledFeatures?.some((feature) => feature.name === 'engagement_employee_pulse_questions') ||
    pulseQuestionFeatureAvailabilityEnabled

  const { has_dev_forfaitjour_in_time_management: hasForfaitJour } = useFeatures([
    TimesheetFeatures.FORFAIT_JOUR,
  ])

  // Fetch contract data only if the employee has forfait jours feature
  // This allows us to determine if the employee is on a daily contract (frForfaitJours)
  const { data: referenceContractData } = useQuery(TimesheetReferenceContractsDocument, {
    variables: {
      employeeIds: employee?.id ? [employee.id] : [],
      referenceOn: moment().format('YYYY-MM-DD'),
    },
    skip: !hasForfaitJour || !employee?.id,
  })

  const contract = referenceContractData?.contracts.referenceContractsConnection.nodes[0]
  // Determine if employee is on daily contract
  const isDaily = hasForfaitJour && !!contract?.frForfaitJours

  // Only check the feature flag if employee is daily (optimization)
  const forfaitJoursEnabled = isDaily
    ? !!enabledFeatures?.some((feature) => feature.name === 'dev_forfaitjour_in_time_management')
    : false

  const pulsSettingEnabled =
    data?.engagement?.settingsConnection?.edges[0]?.node?.employeePulseEnabled ?? false
  const isPulseEnabled = pulseQuestionFeatureEnabled && pulsSettingEnabled

  const pulse = data?.engagement?.employeeCurrentPulsesConnection.edges[0]?.node.pulse

  const isLoading = loading && !data

  const hasTeamStatus = data?.attendance._policies.dashboardWidgetWhoIsInVisible.can ?? false

  const canCreateGroup = data?.posts?._policies?.groupCreate?.can ?? false

  const hasData = !!data

  const timeoffModuleEnabled = !!data?.companies?.moduleAvailabilitiesConnection.nodes?.some(
    (module) => module?.id === 'timeoff' && module?.available
  )

  const widgetConfig = useMemo(() => {
    if (!hasData) return

    // For daily widget: use canSeeOwnedAttendance policy (employee can see their own attendance)
    // For hourly widget: use timeTrackingVisible policy (as before)
    const baseClockInConditionsHourly =
      timeTrackingManagementEnabled && timeTrackingVisible && !!employee?.id
    const baseClockInConditionsDaily =
      timeTrackingManagementEnabled && canSeeOwnedAttendance && !!employee?.id

    // If not daily: show hourly widget (no feature flag check needed)
    const shouldShowDaily = isDaily && forfaitJoursEnabled && baseClockInConditionsDaily
    const shouldShowHourly = !isDaily && baseClockInConditionsHourly

    const isExternalUser = employee?.affiliation === 'external'

    return {
      shouldShowCompanyLinksWidget: companyLinksVisible,
      shouldShowAbsencesWidget: hasTeamStatus,
      shouldShowClockInWidget: shouldShowHourly,
      shouldShowDailyClockInWidget: shouldShowDaily,
      shouldShowInboxWidget: !isExternalUser,
    }
  }, [
    hasData,
    timeTrackingManagementEnabled,
    timeTrackingVisible,
    canSeeOwnedAttendance,
    hasTeamStatus,
    companyLinksVisible,
    employee?.id,
    employee?.affiliation,
    forfaitJoursEnabled,
    isDaily,
  ])

  return (
    <>
      <Dashboard
        widgetConfig={widgetConfig}
        communitiesEnabled={communitiesEnabled}
        isLoading={isLoading}
        pulseEnabled={isPulseEnabled}
        pulse={pulse}
        canCreateGroup={canCreateGroup}
        timeoffModuleEnabled={timeoffModuleEnabled}
      />
    </>
  )
}

export default HomePage
